/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { FontWeights, MessageBar, MessageBarType, Stack } from '@fluentui/react';
import { Property } from 'csstype';
import { observer } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { LuisText } from 'src/core/components/LuisText';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useResponsiveMode } from 'src/core/hooks/useResponsiveMode';
import { ProgressTrackerStore } from 'src/core/stores/ProgressTrackerStore';
import { AMLConnectionInfo } from 'src/customText/core/stores/CustomTextProjectStore';
import { AMLConnectionSteps } from 'src/customText/verticals/azureMLConnectionModal/AzureMLConnectionModal';
import { AzureMLTabConnectionFooter } from 'src/customText/verticals/azureMLConnectionModal/components/AzureMLTabConnectionFooter';

type ReviewPageProps = {
    onNext: () => void;
    onDismiss: () => void;
    onPrevious: () => void;
    createAmlProjectTracker: ProgressTrackerStore;
    initializingAmlWorkspaceProjectTracker: ProgressTrackerStore;
    amlConnectionInfo: AMLConnectionInfo;
};

const messages = defineMessages({
    title: {
        id: 'customText.reviewPage.title',
        defaultMessage: 'Review'
    },
    subTitle: {
        id: 'customText.reviewPage.subTitle',
        defaultMessage: 'Review the settings for your connection to Azure Machine Learning and make any changes as needed.'
    },
    subscription: {
        id: 'customText.reviewPage.subscription',
        defaultMessage: 'Subscription:'
    },
    storageAccount: {
        id: 'customText.reviewPage.storageAccount',
        defaultMessage: 'Storage account:'
    },
    azureBlobDataStore: {
        id: 'customText.reviewPage.azureBlobDataStore',
        defaultMessage: 'Azure blob datastore:'
    },
    azureMLWorkspace: {
        id: 'customText.reviewPage.azureMLWorkspace',
        defaultMessage: 'Azure Machine Learning workspace:'
    },
    projectName: {
        id: 'customText.reviewPage.projectName',
        defaultMessage: 'Project name:'
    },
    labelingTaskType: {
        id: 'customText.reviewPage.labelingTaskType',
        defaultMessage: 'Labeling task type:'
    },
    language: {
        id: 'customText.reviewPage.language',
        defaultMessage: 'Language:'
    },
    incrementalRefresh: {
        id: 'customText.reviewPage.incrementalRefresh',
        defaultMessage: 'Incremental refresh:'
    },
    outsourcing: {
        id: 'customText.reviewPage.outsourcing',
        defaultMessage: 'Outsourcing:'
    },
    labelingInstruction: {
        id: 'customText.reviewPage.labelingInstruction',
        defaultMessage: 'Labeling instructions:'
    },
    none: {
        id: 'customText.reviewPage.none',
        defaultMessage: 'None'
    },
    provided: {
        id: 'customText.reviewPage.provided',
        defaultMessage: 'Provided'
    }
});

const childrenGapConstant = 4;

export const ReviewPage = observer((props: ReviewPageProps) => {
    const { onNext, onPrevious, onDismiss, amlConnectionInfo, createAmlProjectTracker, initializingAmlWorkspaceProjectTracker } = props;
    const { localize } = useLocalization();
    const { isLargeOrGreater } = useResponsiveMode();
    const { rootStore } = useGlobalState();

    const { containerName } = rootStore.currentCustomTextProjectStore;

    return (
        <Stack styles={{ root: { minHeight: '600px' } }}>
            {createAmlProjectTracker.isFailed ? (
                <MessageBar messageBarType={MessageBarType.error} dismissButtonAriaLabel="Close" styles={{ root: { minHeight: 'auto' } }}>
                    {createAmlProjectTracker.error.message}
                </MessageBar>
            ) : null}
            {initializingAmlWorkspaceProjectTracker.isFailed ? (
                <MessageBar messageBarType={MessageBarType.error} dismissButtonAriaLabel="Close" styles={{ root: { minHeight: 'auto' } }}>
                    {(initializingAmlWorkspaceProjectTracker.error as unknown) as string}
                </MessageBar>
            ) : null}
            <Stack
                styles={{ root: { overflowY: 'auto' } }}
                tokens={{ childrenGap: 12, padding: `${isLargeOrGreater ? `0 0 0 32px` : '12px 20px'}` }}>
                <LuisText variant="xLarge" fontWeight={FontWeights.semibold as Property.FontWeight}>
                    {localize(messages.title)}
                </LuisText>
                <LuisText>{localize(messages.subTitle)}</LuisText>
                <Stack horizontal tokens={{ childrenGap: childrenGapConstant }}>
                    <LuisText fontWeight={FontWeights.semibold as Property.FontWeight}>{localize(messages.subscription)}</LuisText>
                    <LuisText>
                        &nbsp;
                        {amlConnectionInfo.subscription}
                    </LuisText>
                </Stack>
                <Stack horizontal tokens={{ childrenGap: childrenGapConstant }}>
                    <LuisText fontWeight={FontWeights.semibold as Property.FontWeight}>{localize(messages.storageAccount)}</LuisText>
                    <LuisText>
                        &nbsp;
                        {amlConnectionInfo.storageAccount}
                    </LuisText>
                </Stack>
                <Stack horizontal tokens={{ childrenGap: childrenGapConstant }}>
                    <LuisText fontWeight={FontWeights.semibold as Property.FontWeight}>{localize(messages.azureBlobDataStore)}</LuisText>
                    <LuisText>
                        &nbsp;
                        {containerName}
                    </LuisText>
                </Stack>
                <Stack horizontal tokens={{ childrenGap: childrenGapConstant }}>
                    <LuisText fontWeight={FontWeights.semibold as Property.FontWeight}>{localize(messages.azureMLWorkspace)}</LuisText>
                    <LuisText>
                        &nbsp;
                        {amlConnectionInfo.azureMLWorkspace}
                    </LuisText>
                </Stack>
                <Stack horizontal tokens={{ childrenGap: childrenGapConstant }}>
                    <LuisText fontWeight={FontWeights.semibold as Property.FontWeight}>{localize(messages.projectName)}</LuisText>
                    <LuisText>
                        &nbsp;
                        {amlConnectionInfo.projectName}
                    </LuisText>
                </Stack>
                <Stack horizontal tokens={{ childrenGap: childrenGapConstant }}>
                    <LuisText fontWeight={FontWeights.semibold as Property.FontWeight}>{localize(messages.labelingTaskType)}</LuisText>
                    <LuisText>
                        &nbsp;
                        {amlConnectionInfo.labelingTaskType}
                    </LuisText>
                </Stack>
                <Stack horizontal tokens={{ childrenGap: childrenGapConstant }}>
                    <LuisText fontWeight={FontWeights.semibold as Property.FontWeight}>{localize(messages.language)}</LuisText>
                    <LuisText>
                        &nbsp;
                        {amlConnectionInfo.projectLanguage}
                    </LuisText>
                </Stack>
                <Stack horizontal tokens={{ childrenGap: childrenGapConstant }}>
                    <LuisText fontWeight={FontWeights.semibold as Property.FontWeight}>{localize(messages.incrementalRefresh)}</LuisText>
                    <LuisText>&nbsp; Enabled</LuisText>
                </Stack>
                <Stack horizontal tokens={{ childrenGap: childrenGapConstant }}>
                    <LuisText fontWeight={FontWeights.semibold as Property.FontWeight}>{localize(messages.outsourcing)}</LuisText>
                    <LuisText>
                        &nbsp;
                        {amlConnectionInfo.vendor || localize(messages.none)}
                    </LuisText>
                </Stack>
                <Stack horizontal tokens={{ childrenGap: childrenGapConstant }}>
                    <LuisText fontWeight={FontWeights.semibold as Property.FontWeight}>{localize(messages.labelingInstruction)}</LuisText>
                    <LuisText>
                        &nbsp;
                        {amlConnectionInfo.labelingInstructionText || amlConnectionInfo.labelingInstructionUrl
                            ? localize(messages.provided)
                            : localize(messages.none)}
                    </LuisText>
                </Stack>
            </Stack>
            <AzureMLTabConnectionFooter
                onNext={onNext}
                onDismiss={onDismiss}
                onPrevious={onPrevious}
                activeStep={AMLConnectionSteps.Review}
                isLoading={createAmlProjectTracker.isRunning || initializingAmlWorkspaceProjectTracker.isRunning}
            />
        </Stack>
    );
});
